import { Box, Typography, Avatar, alpha, useTheme } from '@mui/material'
import { Lock, Map as MapIcon, People, ChevronRight } from '@mui/icons-material'
import { useNavigate } from 'react-router-dom'
import { ChatReferenceKind, type ChatReferenceDto } from '@api/types/chat'

/**
 * An adventure or group a message points at, as *this* reader is allowed to see it.
 *
 * The server decides, per reader, every read. Somebody who is not entitled to it receives
 * `visible: false` with no descriptive fields at all — so there is nothing here to hide, and
 * nothing to be tempted to fill in from a cache. Two people in one conversation can be owed
 * different answers; that is the feature, not an edge case.
 *
 * The unavailable card is deliberately dull and identical whatever the reason. "You can't see
 * this" must not shade into "…because it's invite-only" or "…because it was deleted" — the
 * distinction is itself information about something they were not shown.
 */

interface ChatReferenceCardProps {
  reference: ChatReferenceDto
  /** Own messages sit on a coloured bubble, so the card has to stay legible on it. */
  onOwnBubble?: boolean
}

export function ChatReferenceCard({ reference, onOwnBubble }: ChatReferenceCardProps) {
  const theme = useTheme()
  const navigate = useNavigate()

  const isGroup = reference.kind === ChatReferenceKind.Group
  const onBubble = Boolean(onOwnBubble)

  const borderColor = onBubble
    ? alpha(theme.palette.primary.contrastText, 0.35)
    : theme.palette.divider
  const titleColor = onBubble ? theme.palette.primary.contrastText : theme.palette.text.primary
  const bodyColor = onBubble
    ? alpha(theme.palette.primary.contrastText, 0.8)
    : theme.palette.text.secondary

  if (!reference.visible) {
    return (
      <Box
        sx={{
          mt: 1, p: 1.25, display: 'flex', alignItems: 'center', gap: 1,
          border: 1, borderColor, borderRadius: 2, opacity: 0.75,
        }}
      >
        <Lock sx={{ fontSize: 16, color: bodyColor }} />
        <Typography variant="body2" sx={{ fontStyle: 'italic', color: bodyColor }}>
          {isGroup ? 'A group you can’t see' : 'An adventure you can’t see'}
        </Typography>
      </Box>
    )
  }

  const open = () => {
    navigate(isGroup ? `/groups/${reference.id}` : `/adventures/${reference.id}`)
  }

  return (
    <Box
      role="button"
      tabIndex={0}
      onClick={open}
      onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); open() } }}
      sx={{
        mt: 1, p: 1.25, display: 'flex', alignItems: 'center', gap: 1.25,
        border: 1, borderColor, borderRadius: 2, cursor: 'pointer',
        transition: 'background-color 120ms',
        '&:hover': {
          bgcolor: onBubble
            ? alpha(theme.palette.primary.contrastText, 0.12)
            : theme.palette.action.hover,
        },
      }}
    >
      {reference.imageUrl ? (
        <Avatar src={reference.imageUrl} variant="rounded" sx={{ width: 40, height: 40 }} />
      ) : (
        <Avatar variant="rounded" sx={{ width: 40, height: 40, bgcolor: alpha(titleColor, 0.15) }}>
          {isGroup
            ? <People sx={{ fontSize: 20, color: titleColor }} />
            : <MapIcon sx={{ fontSize: 20, color: titleColor }} />}
        </Avatar>
      )}

      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Typography
          sx={{ fontSize: 10, fontWeight: 700, letterSpacing: 0.5, textTransform: 'uppercase', color: bodyColor }}
        >
          {isGroup ? 'Group' : 'Adventure'}
        </Typography>
        <Typography
          sx={{ fontSize: 14, fontWeight: 800, color: titleColor, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
        >
          {reference.title}
        </Typography>
        {!!reference.description && (
          <Typography
            sx={{
              fontSize: 12, color: bodyColor, display: '-webkit-box',
              WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden',
            }}
          >
            {reference.description}
          </Typography>
        )}
      </Box>

      <ChevronRight sx={{ fontSize: 20, color: bodyColor }} />
    </Box>
  )
}
